import React, { useMemo, useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, EyeIcon, DownloadIcon, FilterIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, FileTextIcon, TerminalIcon, RefreshCwIcon, MonitorIcon, XIcon, MoreVerticalIcon } from 'lucide-react';
import { Sale, TransactionSource, TransactionStatus } from '../../../shared/types/Transaction';
import { RefundModal } from '../../refunds/components/RefundModal';
import { buildMockSales } from '../../../shared/mocks/mockSales';
import { formatMoney } from '../../../shared/lib/money';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { getAvailableBalance } from '../../refunds/utils/refundFunding';
export function Sales() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useCompany();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedSaleForRefund, setSelectedSaleForRefund] = useState<Sale | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Use shared mock sales fixtures (single source of truth)
  const sales: Sale[] = useMemo(() => buildMockSales(), []);
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setPaymentMethodFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
  };
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || sourceFilter !== 'all' || paymentMethodFilter !== 'all' || dateFrom !== '' || dateTo !== '' || amountMin !== '' || amountMax !== '';
  const getSourceIcon = (source: TransactionSource) => {
    const icons = {
      payment_link: <CreditCardIcon size={16} data-id="element-12651" data-mp="cbfu26d8t7y7" />,
      qr_code: <QrCodeIcon size={16} data-id="element-12652" data-mp="caeje3052nh0" />,
      tap_to_phone: <SmartphoneIcon size={16} data-id="element-12653" data-mp="7d5xpsfs81b0" />,
      invoice: <FileTextIcon size={16} data-id="element-12654" data-mp="7c361iamxuu1" />,
      pos: <TerminalIcon size={16} data-id="element-12655" data-mp="7b1ulcppj6bs" />,
      recurring: <RefreshCwIcon size={16} data-id="element-12656" data-mp="m0r01k4u1whv" />,
      virtual_terminal: <MonitorIcon size={16} data-id="element-12657" data-mp="m3lk3ajxpfar" />
    };
    return icons[source];
  };
  const getSourceLabel = (source: TransactionSource) => {
    const labels = {
      payment_link: 'Payment Link',
      qr_code: 'QR Code',
      tap_to_phone: 'Tap to Phone',
      invoice: 'Invoice',
      pos: 'POS Terminal',
      recurring: 'Recurring',
      virtual_terminal: 'Virtual Terminal'
    };
    return labels[source];
  };
  const getPaymentMethodCategory = (paymentMethod: Sale['paymentMethod']) => {
    const categories: Record<string, string> = {
      card: 'Card',
      wallet: 'E-wallet',
      bank_transfer: 'Bank transfer',
      cash: 'Cash'
    };
    return categories[paymentMethod.type] || paymentMethod.type;
  };
  const getPaymentMethodName = (paymentMethod: Sale['paymentMethod']) => {
    if (paymentMethod.brand) return paymentMethod.brand;
    if (paymentMethod.details) return paymentMethod.details;
    const fallback: Record<string, string> = {
      card: 'Card',
      wallet: 'E-wallet',
      bank_transfer: 'Bank transfer',
      cash: 'Cash'
    };
    return fallback[paymentMethod.type] || paymentMethod.type;
  };
  const getStatusBadge = (status: TransactionStatus) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      partially_refunded: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-12658" data-mp="3rch4fvhmjnd7">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || sale.source === sourceFilter;
    const matchesPaymentMethod = paymentMethodFilter === 'all' || sale.paymentMethod.type === paymentMethodFilter;
    const saleDate = new Date(sale.date);
    const matchesDateFrom = !dateFrom || saleDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || saleDate <= new Date(dateTo + 'T23:59:59');
    const matchesAmountMin = !amountMin || sale.amount >= parseFloat(amountMin);
    const matchesAmountMax = !amountMax || sale.amount <= parseFloat(amountMax);
    return matchesSearch && matchesStatus && matchesSource && matchesPaymentMethod && matchesDateFrom && matchesDateTo && matchesAmountMin && matchesAmountMax;
  });
  const totalVolume = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);
  const totalTransactions = filteredSales.length;
  const successfulTransactions = filteredSales.filter(s => s.status === 'completed').length;
  const successRate = totalTransactions > 0 ? Math.round(successfulTransactions / totalTransactions * 100) : 0;
  // Derive currency from first sale (assume single-currency list)
  const currency = filteredSales[0]?.currency || getTenant().currency.code;
  // Refunds are funded from the merchant's available balance — the same
  // figure Balance summary shows.
  const availableBalance = getAvailableBalance(selectedCompany);
  const handleRefund = (amount: number, reason: string, notes: string) => {
    console.log('Refund initiated:', {
      transactionId: selectedSaleForRefund?.transactionId,
      amount,
      reason,
      notes,
      availableBalance
    });
    // In a real app, this would call an API to process the refund
    // For now, we'll just close the modal and show a success message
    alert(`Refund of ${formatMoney(amount, selectedSaleForRefund?.currency)} initiated successfully!`);
  };
  const handleOpenRefundModal = (sale: Sale) => {
    setSelectedSaleForRefund(sale);
    setRefundModalOpen(true);
  };
  return <div className="space-y-6" data-id="element-12659" data-mp="kgjqpzt4bijd">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-12660" data-mp="2w46j4512tve4">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12661" data-mp="2w2lpqiqi0ihu">
          <div className="text-sm font-medium text-gray-500" data-id="element-12662" data-mp="30w6my8k9p8fz">
            {t('pages.Sales.total_volume')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12663" data-mp="2n1xa5jlgia2u">
            {formatMoney(totalVolume, currency)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12664" data-mp="3euy09srb2q32">
          <div className="text-sm font-medium text-gray-500" data-id="element-12665" data-mp="3juak04mvrh1f">
            {t('pages.Sales.total_transactions')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12666" data-mp="35vavqy3a545s">
            {totalTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12667" data-mp="3as1czoqtm5ss">
          <div className="text-sm font-medium text-gray-500" data-id="element-12668" data-mp="1ueb66a8bh3fg">
            {t('pages.Sales.successful')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-12669" data-mp="1zg0kpzuqzz8a">
            {successfulTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12670" data-mp="2m2xffv8aiuqx">
          <div className="text-sm font-medium text-gray-500" data-id="element-12671" data-mp="2m1mcfxmo5r0v">
            {t('pages.Sales.success_rate')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12672" data-mp="2h5nqshsq6n45">
            {successRate}%
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-12673" data-mp="39ryu407pv24n">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-12674" data-mp="34uq7nnfjcbxl">
          <div className="space-y-4" data-id="element-12675" data-mp="302f8imsmcu63">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-12676" data-mp="300c8d9xr5knn">
              <div className="flex-1 max-w-lg" data-id="element-12677" data-mp="2v1rj25k1gldz">
                <div className="relative" data-id="element-12678" data-mp="3sk1cph3x4shj">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-12679" data-mp="3sl2ppr4hvp1w" />

                  <input type="text" placeholder={t('pages.Sales.attrs.search_by_transaction_id_customer_name')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-12680" data-mp="3v4z1q5oi6ya4" />
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-12681" data-mp="mu5tofyfc8qr">
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showAdvancedFilters ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} data-id="element-12682" data-mp="rmfifsnsz2rg">
                  <FilterIcon size={18} data-id="element-12683" data-mp="15lp06wrnq4xj" />
                  {t('pages.Sales.filters')}
                  {hasActiveFilters && <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full" data-id="element-12684" data-mp="2vzviwn1k62c">
                      {[searchTerm, statusFilter !== 'all', sourceFilter !== 'all', paymentMethodFilter !== 'all', dateFrom, dateTo, amountMin, amountMax].filter(Boolean).length}
                    </span>}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-12685" data-mp="vh9efjz4mm8g">
                  <DownloadIcon size={18} data-id="element-12686" data-mp="2xaz04iv48yfz" />
                  {t('pages.Sales.export')}
                </button>
              </div>
            </div>
            {/* Advanced Filters Panel */}
            {showAdvancedFilters && <div className="p-4 bg-gray-50 rounded-lg space-y-4" data-id="element-12687" data-mp="2uqsw6b6i57zf">
                <div className="flex items-center justify-between" data-id="element-12688" data-mp="2pyrbq1z6kayb">
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-12689" data-mp="2pxg7k11tp16o">
                    {t('pages.Sales.advanced_filters')}
                  </h3>
                  {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-12690" data-mp="2fz0jy8208a1x">
                      <XIcon size={16} aria-hidden="true" data-id="element-12691" data-mp="38npka4v8kbcm" />
                      {t('pages.Sales.clear_all')}
                    </button>}
                </div>
                {/* Filter Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-12692" data-mp="1sbix8qxlk25t">
                  {/* Status Filter */}
                  <div data-id="element-12693" data-mp="1nbxv67n4rdov">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12694" data-mp="1nbo7iqpk5imc">
                      {t('components.CampaignList.status')}
                    </label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12695" data-mp="jf3v2j79fcr0">
                      <option value="all" data-id="element-12696" data-mp="3rtcilcuxddcb">{t('pages.Sales.all_status')}</option>
                      <option value="completed" data-id="element-12697" data-mp="g9fx93vwaab">
                        {t('components.getOrderStatusBadge.completed')}
                      </option>
                      <option value="pending" data-id="element-12698" data-mp="3irmhtgf5l21v">
                        {t('pages.Sales.pending')}
                      </option>
                      <option value="processing" data-id="element-12699" data-mp="38vt804stjmns">
                        {t('pages.OrderDetail.processing')}
                      </option>
                      <option value="failed" data-id="element-12700" data-mp="3dtb8r7womvkp">{t('pages.Sales.failed')}</option>
                      <option value="refunded" data-id="element-12701" data-mp="2u82np6rr18mh">
                        {t('pages.Sales.refunded')}
                      </option>
                    </select>
                  </div>
                  {/* Source Filter */}
                  <div data-id="element-12702" data-mp="347thf68hjjli">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12703" data-mp="344xj3gsq6swj">
                      {t('pages.SaleDetail.source')}
                    </label>
                    <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12704" data-mp="3d6nd8udzs5y0">
                      <option value="all" data-id="element-12705" data-mp="3hyym4mqpn22p">
                        {t('pages.Sales.all_sources')}
                      </option>
                      <option value="payment_link" data-id="element-12706" data-mp="nxg2udol8fn">
                        {t('pages.Sales.payment_link')}
                      </option>
                      <option value="qr_code" data-id="element-12707" data-mp="1papi8jxpp7r2">
                        {t('pages.Sales.qr_code')}
                      </option>
                      <option value="tap_to_phone" data-id="element-12708" data-mp="1u7fyp8dfi5d0">
                        {t('pages.Sales.tap_to_phone')}
                      </option>
                      <option value="invoice" data-id="element-12709" data-mp="23yu1pq4l9naw">
                        {t('pages.SaleDetail.invoice')}
                      </option>
                      <option value="pos" data-id="element-12710" data-mp="16c4w76aogska">
                        {t('pages.Sales.pos_terminal')}
                      </option>
                      <option value="recurring" data-id="element-12711" data-mp="1baesjk851z7h">
                        {t('pages.Sales.recurring')}
                      </option>
                      <option value="virtual_terminal" data-id="element-12712" data-mp="1l8ceu26ftlxg">
                        {t('pages.Sales.virtual_terminal')}
                      </option>
                    </select>
                  </div>
                  {/* Payment Method Filter */}
                  <div data-id="element-12713" data-mp="2wm6dr6d4icpy">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12714" data-mp="2wplwqk9x45wn">
                      {t('pages.OrderDetail.payment_method')}
                    </label>
                    <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12715" data-mp="g2duvap5y2ax">
                      <option value="all" data-id="element-12716" data-mp="1a2c0n0gu2ep">
                        {t('pages.Sales.all_methods')}
                      </option>
                      <option value="card" data-id="element-12717" data-mp="3ogmkqb0aweze">{t('pages.Sales.card')}</option>
                      <option value="wallet" data-id="element-12718" data-mp="3oipg7ak830bi">{t('pages.Sales.wallet')}</option>
                      <option value="bank_transfer" data-id="element-12719" data-mp="3jjm9llolgm1f">
                        {t('pages.Sales.bank_transfer')}
                      </option>
                      <option value="cash" data-id="element-12720" data-mp="1hqof7lf65led">{t('pages.Sales.cash')}</option>
                    </select>
                  </div>
                  {/* Date From */}
                  <div data-id="element-12721" data-mp="3c5bwco38sbgm">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12722" data-mp="3gxd90b4bo476">
                      {t('pages.Sales.date_from')}
                    </label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12723" data-mp="32aab43c37tjb" />
                  </div>
                  {/* Date To */}
                  <div data-id="element-12724" data-mp="3kyfcx596p4fh">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12725" data-mp="3l1b5sf66d2x2">
                      {t('pages.Sales.date_to')}
                    </label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12726" data-mp="29j0ahly7hkcz" />
                  </div>
                  {/* Amount Min */}
                  <div data-id="element-12727" data-mp="15kmzkmfg80kq">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12728" data-mp="15jtya0z9e39z">
                      {t('pages.Sales.min_amount_usd')}
                    </label>
                    <input type="number" placeholder="0" value={amountMin} onChange={e => setAmountMin(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12729" data-mp="dvjrswocs83c" />
                  </div>
                  {/* Amount Max */}
                  <div data-id="element-12730" data-mp="3q9ssl7ndsdu4">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12731" data-mp="3mb3h5klwl6d4">
                      {t('pages.Sales.max_amount_usd')}
                    </label>
                    <input type="number" placeholder={t('pages.Sales.attrs.no_limit')} value={amountMax} onChange={e => setAmountMax(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12732" data-mp="f2batiubosl7" />
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-12733" data-mp="17odw6uyc3c3n">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-12734" data-mp="17pfixwoppn17">
            <thead className="bg-gray-50" data-id="element-12735" data-mp="1bp68jx2i5x45">
              <tr data-id="element-12736" data-mp="1bsbzlhuoz92i">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12737" data-mp="1gpkgzg80n92p">
                  {t('pages.OrderDetail.transaction_id')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12738" data-mp="1vsc4qes8cqm3">
                  {t('pages.Sales.date_time')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12739" data-mp="1qvbyo3vdrnuz">
                  {t('pages.Orders.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12740" data-mp="1hu5glqd324zm">
                  {t('pages.SaleDetail.source')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12741" data-mp="29ks1wyyg9uf3">
                  {t('pages.Sales.payment_category')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12742" data-mp="20s88ccx0mg14">
                  {t('pages.OrderDetail.payment_method')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12743" data-mp="2si2yucwm0brd">
                  {t('pages.Sales.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12744" data-mp="iwh5fxc211bp">
                  {t('components.CampaignList.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12745" data-mp="dz7gsjwhotox">
                  {t('pages.CampaignDetails.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-12746" data-mp="11mfb66sdqic6">
              {filteredSales.map(sale => <tr key={sale.id} onClick={() => navigate(`/sale-detail/${sale.id}`)} className="hover:bg-gray-50 cursor-pointer" data-id="element-12747" data-mp="wvgrqyc3h44p">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12748" data-mp="1pdl1jfvy4t0q">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12749" data-mp="1klt9t2120h90">
                      {sale.transactionId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12750" data-mp="1rysgxt5hn7zj">
                    <div className="text-sm text-gray-900" data-id="element-12751" data-mp="1wv968qofbse7">
                      {new Date(sale.date).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-12752" data-mp="21xhz7n1aqugb">
                      {new Date(sale.date).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12753" data-mp="2fsttjtbjiiwp">
                    <div className="text-sm text-gray-900" data-id="element-12754" data-mp="2kmy22h0geyzw">
                      {sale.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-12755" data-mp="2pmstclo55qlh">
                      {sale.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12756" data-mp="2ykmohta66e72">
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-12757" data-mp="2yj1qtusk6f4z">
                      {getSourceIcon(sale.source)}
                      {getSourceLabel(sale.source)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12758" data-mp="1897zl6oqv57z">
                    <div className="text-sm text-gray-900" data-id="element-12759" data-mp="18asrspnl6c2o">
                      {getPaymentMethodCategory(sale.paymentMethod)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12760" data-mp="1i5b2wfvc19wi">
                    <div className="text-sm text-gray-900" data-id="element-12761" data-mp="1d6zr3zlh8pbn">
                      {getPaymentMethodName(sale.paymentMethod)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12762" data-mp="1n6i9wcpni524">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12763" data-mp="1n44fm62sowvm">
                      {formatMoney(sale.amount, sale.currency)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12764" data-mp="3oz6hey9bvenp">
                    {getStatusBadge(sale.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right" onClick={e => e.stopPropagation()} data-id="element-12765" data-mp="3iv8uy3de3bo7">
                    <div className="relative inline-block" data-id="element-12766" data-mp="353lnr8hwsiqn">
                      <button aria-label="Sale actions" aria-expanded={openMenuId === sale.id} onClick={() => setOpenMenuId(openMenuId === sale.id ? null : sale.id)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-12767" data-mp="3a258x6oohqhy">
                        <MoreVerticalIcon size={18} className="text-gray-600" aria-hidden="true" data-id="element-12768" data-mp="2r3aw438rz04f" />
                      </button>

                      {/* Dropdown Menu */}
                      {openMenuId === sale.id && <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} data-id="element-12769" data-mp="2ldhx1tpkyzwo" />

                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12770" data-mp="2bfv7mdannpyg">
                            <button onClick={() => {
                        setOpenMenuId(null);
                        navigate(`/sale-detail/${sale.id}`);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12771" data-mp="22gq8b4r9rsl8">
                              <EyeIcon size={16} data-id="element-12772" data-mp="1jqiifockyymz" />
                              {t('pages.Sales.view_details')}
                            </button>
                            {sale.status === 'completed' && <button onClick={() => {
                        setOpenMenuId(null);
                        handleOpenRefundModal(sale);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12773" data-mp="3qfah3wl1a2iu">
                                <RefreshCwIcon size={16} data-id="element-12774" data-mp="37oj8wycs8i61" />
                                {t('pages.SaleDetail.refund')}
                              </button>}
                          </div>
                        </>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredSales.length === 0 && <div className="text-center py-12" data-id="element-12775" data-mp="2nyfy5gnfww5m">
            <p className="text-gray-500" data-id="element-12776" data-mp="2syllwlql9yif">
              {t('pages.Sales.no_transactions_found')}
            </p>
          </div>}
      </div>
      {/* Refund Modal */}
      {selectedSaleForRefund && <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} transactionAmount={selectedSaleForRefund.amount} transactionId={selectedSaleForRefund.transactionId} currency={getTenant().currency.code} availableBalance={availableBalance} onRefund={handleRefund} data-id="element-12777" data-mp="3raemfpttxulq" />}
    </div>;
}